/* sts-fit.js — the client-side copy of the ONE fit verdict.
 *
 * Spec 01 §3.7, §3.10, §3.11. The server-side copy is
 * snippets/sts-product-fit.liquid; this is the same rule for the surfaces that
 * only ever see a product after it has been rendered — a guest's collection
 * card (assets/sts-collection.js) and Boost's search results
 * (assets/sts-search-fit.js), neither of which has a customer type at render
 * time.
 *
 * It exists so there is exactly one place the rule can be read or changed.
 * assets/sts-collection.js carried its own `barredLetter` / `missingSubs` /
 * `mySubs` until this file; the Boost decorator would have been a third. Three
 * copies of a rule that decides whether a shopper is told a product is wrong
 * for their skin is three chances for two surfaces to disagree about the same
 * product, which is the one failure the whole verdict design exists to prevent.
 *
 * ── What it is NOT ──────────────────────────────────────────────────────────
 * It holds no data and fetches nothing. The caller supplies the product's
 * approved type list and subtype list — from the server-rendered
 * data-sts-approved / data-sts-sens attributes on a v4 card, or from the tags
 * on a Boost card — and the shopper's type from window.STS.state. Colours,
 * copy and markup belong to the caller too.
 *
 * ── Usage ───────────────────────────────────────────────────────────────────
 *   var FIT = window.STS.fit;
 *   var v = FIT.verdict(['OSPT','OSPW'], ['S1','S2'], 'OSPT', FIT.wantedSubs());
 *   v.fit      approved | subtype | barred | unknown | unrated
 *   v.letter   the barred letter, only when fit === 'barred' ('*' when no
 *              single letter explains it)
 *   v.missing  the subtype NAMES the product does not carry, when fit === 'subtype'
 *
 *   FIT.fromTags(['OSPT','S1','Cleanser'])  -> { approved: ['OSPT'], sens: ['S1'] }
 *   FIT.wantedSubs()                        -> ['S1','S3'] from this device
 */
(function (w) {
  'use strict';

  var STS = (w.STS = w.STS || {});
  /* Two script tags for this file on one page (a section that loads it beside
     another that does) must not rebuild the object other modules already hold. */
  if (STS.fit) return;

  var TYPES = 'DSPT DSNT DSPW DSNW OSPT OSNT OSPW OSNW ORPT ORNT ORPW ORNW DRPT DRNT DRPW DRNW'.split(' ');
  var IS_TYPE = {};
  for (var t = 0; t < TYPES.length; t++) IS_TYPE[TYPES[t]] = true;

  var SUBS = { S1: 'Acne', S2: 'Redness', S3: 'Stinging', S4: 'Allergic' };
  var SUB_ORDER = ['S1', 'S2', 'S3', 'S4'];

  /* D<->O, S<->R, P<->N, W<->T — the same flip table as snippets/sts-skin-types.liquid. */
  var FLIP = { D: 'O', O: 'D', S: 'R', R: 'S', P: 'N', N: 'P', W: 'T', T: 'W' };

  /* The barred letter, derived exactly as snippets/sts-product-fit.liquid derives
     it server-side, so the two paths can never produce different copy for the
     same product: hard bars first (a letter no approved type contains), then the
     letters whose single flip lands on an approved type, then '*'.

     The hard-bar test runs against the JOINED list, not the array. The copy this
     replaced in assets/sts-collection.js asked `approved.indexOf(letter)` of an
     array of four-letter codes, which is -1 for a single letter every time — so
     every barred guest card returned type.charAt(0) and was given the D/O reason
     sentence whatever the product's list actually was. Liquid asks
     `sts_f_approved contains sts_f_at` of the space-joined string, which is the
     real test: OSPT against [DSPT DSNT DSPW DSNW ORPT] is not barred on O — ORPT
     has one — it is barred on S. */
  function barredLetter(approved, type) {
    var joined = approved.join(' ');
    var i, at;
    for (i = 0; i < 4; i++) {
      at = type.charAt(i);
      if (joined.indexOf(at) === -1) return at;
    }
    for (i = 0; i < 4; i++) {
      at = type.charAt(i);
      var candidate = type.slice(0, i) + FLIP[at] + type.slice(i + 1);
      if (approved.indexOf(candidate) > -1) return at;
    }
    return '*';
  }

  /* The subtype NAMES the shopper needs and the product does not carry.
     R1 (resistant) is never in `wanted`: it is the absence of a subtype
     constraint, not a subtype a product has to carry (04-Q7). */
  function missingSubs(cardSubs, wanted) {
    var out = [];
    for (var i = 0; i < wanted.length; i++) {
      if (cardSubs.indexOf(wanted[i]) === -1) out.push(SUBS[wanted[i]]);
    }
    return out;
  }

  /* Workstream 02's state object carries the five state facts only. The
     sensitivity list is a separate device fact, so it is read directly and
     accepted in either of the two shapes the quiz has written over the years. */
  function wantedSubs() {
    var raw = null;
    try { raw = w.localStorage.getItem('customerSensitivityList'); } catch (e) { raw = null; }
    var out = [];
    if (!raw) return out;
    var parsed = null;
    try { parsed = JSON.parse(raw); } catch (e) { parsed = raw.split(','); }
    if (!parsed) return out;
    if (!parsed.length && typeof parsed === 'object') parsed = [];
    for (var i = 0; i < parsed.length; i++) {
      var c = String(parsed[i]).trim().toUpperCase();
      if (SUBS[c]) out.push(c);
    }
    return out;
  }

  /* A product's approval read off its TAGS — the fallback vocabulary
     snippets/sts-product-fit.liquid already accepts, and the only one available
     on a card an app rendered from its own index.

     Audited against the live catalogue on 2026-09-19, all 1,818 storefront
     products through the Storefront API: 1,095 carry both the canonical
     metafield `product_info.baumann_skin_type_info` and the 16 type tags and
     they agree on every one; 723 carry neither and are `unrated` either way;
     none carries one without the other. The metafield is the one source
     (snippets/sts-product-fit.liquid reads it alone, as STP's approval check
     does); the tags are the store's mirror of it, re-checked 2026-10-20 equal
     on all 1,116 rated products. A product that ever carries the list without
     the tags reads as `unrated` here, which is silence, not a wrong verdict.

     Subtypes come from the S1-S4 tags for the same reason: `product_info.sensitivity`
     is not exposed to the Storefront API at all and `sensitivity_object` is a
     metaobject reference, while the S tags are on the card already. */
  function fromTags(tags) {
    var approved = [], sens = [], i, code;
    if (!tags) return { approved: approved, sens: sens };
    for (i = 0; i < tags.length; i++) {
      code = String(tags[i]).trim().toUpperCase();
      if (IS_TYPE[code]) {
        if (approved.indexOf(code) === -1) approved.push(code);
      } else if (SUBS[code] && sens.indexOf(code) === -1) {
        sens.push(code);
      }
    }
    /* S1-S4 order whichever order the tags arrived in, so the same product
       always produces the same list. */
    sens.sort(function (a, b) { return SUB_ORDER.indexOf(a) - SUB_ORDER.indexOf(b); });
    return { approved: approved, sens: sens };
  }

  /* The verdict, in snippets/sts-product-fit.liquid's own order.

     `unrated` is not `barred`, and the order is why: a product with no approval
     data at all is unrated for everyone, before the shopper's type is even
     looked at. 723 of the 1,818 storefront products are in that state
     (accessories, supplements, devices, gift cards) and must never be told to a
     shopper as "not for your type". */
  function verdict(approved, sens, type, wanted) {
    if (!approved || !approved.length) return { fit: 'unrated', letter: '', missing: [] };
    if (!type || !IS_TYPE[type]) return { fit: 'unknown', letter: '', missing: [] };
    if (approved.indexOf(type) === -1) {
      return { fit: 'barred', letter: barredLetter(approved, type), missing: [] };
    }
    var missing = missingSubs(sens || [], wanted || []);
    return { fit: missing.length ? 'subtype' : 'approved', letter: '', missing: missing };
  }

  STS.fit = {
    TYPES: TYPES,
    SUBS: SUBS,
    FLIP: FLIP,
    isType: function (code) { return !!IS_TYPE[code]; },
    barredLetter: barredLetter,
    missingSubs: missingSubs,
    wantedSubs: wantedSubs,
    fromTags: fromTags,
    verdict: verdict
  };
}(window));
